import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import OtpInput from './OtpInput';
import styles from './LoginPage.module.css';
import { useLogin, useVerifyOtp, useResendOtp } from './hooks/useAuth';
import { AUTH_ERRORS, RESEND_COOLDOWN_SECONDS } from '../../constants';

interface LoginPageProps {
  onLoginSuccess?: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');

  // Hooks
  const { login, loading: loginLoading } = useLogin();
  const { verifyOtp, loading: verifyLoading } = useVerifyOtp();
  const { resendOtp, loading: resendLoading } = useResendOtp();

  // Local state for UI feedback
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResendSuccess, setIsResendSuccess] = useState(false);

  // Effect for the resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer); // Cleanup on unmount or re-render
    }
  }, [resendCooldown]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const { success, error: loginError } = await login(email);

    if (success) {
      setStep('otp');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } else {
      setError(loginError || AUTH_ERRORS.GENERIC_ERROR);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length < 6) return;

    setError('');
    const success = await verifyOtp(email, otp);

    if (success) {
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    const success = await resendOtp(email);
    if (success) {
      setOtp('');
      setError('');
      setIsResendSuccess(true);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setTimeout(() => setIsResendSuccess(false), 3000);
    }
  };

  const isLoading = loginLoading || verifyLoading || resendLoading;

  const renderEmailStep = () => (
    <form onSubmit={handleEmailSubmit}>
      <div className={styles.loginPanelHeader}>
        <h1 className={styles.loginPanelTitle} data-testid="login-title">ShaktiSetu</h1>
        <h2 className={styles.loginPanelSubtitle} data-testid="login-subtitle">Employer Portal Login</h2>
      </div>
      <div className={styles.formGroup}>
        <label htmlFor="email" className={styles.formLabel}>
          Email Address
        </label>
        <input
          id="email"
          type="email"
          className={styles.formInput}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="e.g., username@shaktisetu.com"
          required
          data-testid="login-email-input"
        />
      </div>
      {error === AUTH_ERRORS.REGISTRATION_DISABLED && (
        <p className={styles.errorMessage} data-testid="login-error-message">
          You are not registered with us yet,{' '}
          <button
            type="button"
            className={styles.contactUsLink}
            onClick={() => navigate(`/register?email=${encodeURIComponent(email)}`)}
            data-testid="login-contact-us-link"
          >
            contact us
          </button>{' '}
          to start your hiring journey.
        </p>
      )}
      {error && error !== AUTH_ERRORS.REGISTRATION_DISABLED && (
        <p className={styles.errorMessage} data-testid="login-error-message">
          {error === AUTH_ERRORS.GENERIC_ERROR
            ? 'Internal server error. Please try again in some time'
            : error}
        </p>
      )}
      <button type="submit" className={styles.buttonPrimary} disabled={isLoading} data-testid="login-submit-button">
        {isLoading ? 'Sending...' : 'Send Login Code'}
      </button>

      <p className={styles.loginPanelFooterText} data-testid="login-footer-text">
        We'll send a one-time code to your email for a secure, passwordless login.
      </p>
    </form>
  );

  const renderOtpStep = () => (
    <form onSubmit={handleOtpSubmit}>
      <div className={styles.loginPanelHeader}>
        <h1 className={styles.loginPanelTitle} data-testid="otp-title">Check your email</h1>
        <p className={styles.loginPanelSubtitle} data-testid="otp-subtitle">
          We've sent a 6-digit code to <br />
          <strong>{email}</strong>
        </p>
      </div>
      <div className={styles.formGroup}>
        <OtpInput otp={otp} setOtp={setOtp} hasError={!!error} />
        {error && <p className={styles.errorMessage} data-testid="login-error-message">{error}</p>}
      </div>
      <button type="submit" className={styles.buttonPrimary} disabled={isLoading || otp.length < 6} data-testid="login-verify-button">
        {isLoading ? 'Verifying...' : 'Verify & Log In'}
      </button>
      <p className={styles.loginPanelFooterText} data-testid="otp-footer-text">
        {isResendSuccess ? (
          <span className={styles.resendFeedback} data-testid="login-resend-success">✓ New code sent!</span>
        ) : (
          <>
            Didn't receive a code?{' '}
            <button
              type="button"
              className={`${styles.resendButton} ${resendCooldown > 0 ? styles.resendButtonDisabled : ''}`}
              onClick={handleResend}
              disabled={resendCooldown > 0}
                data-testid="login-resend-button"
            >
              Resend {resendCooldown > 0 ? `(${resendCooldown}s)` : ''}
            </button>
          </>
        )}
      </p>
    </form>
  );

  return (
    <div className={styles.loginPage}>
      <div className={styles.loginPanel}>
        {step === 'email' ? renderEmailStep() : renderOtpStep()}
      </div>
    </div>
  );
};

export default LoginPage;
